import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getMembership, memberships, getBundlePaymentLink } from '@/data/memberships';
import { useAuth } from '@/contexts/MyContext';
import { useToast } from '@/hooks/use-toast';
import { Check, AlertCircle, CreditCard, ArrowRight, Sparkles } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { productDescriptions } from '@/components/ProductDetails';
import UpsellCard from '@/components/upsell/UpsellCard';

const BlinkingArrow = () => (
  <div className="animate-pulse ml-2">
    <ArrowRight className="h-5 w-5 text-red-600 font-extrabold" />
  </div>
);

const Purchase = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const membership = getMembership(id || '');
  
  const [isLoading, setIsLoading] = useState(false);
  
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [showUpsell, setShowUpsell] = useState(false);
  const [currentUpsell, setCurrentUpsell] = useState<string | null>(null);
  const [includeBonus, setIncludeBonus] = useState(false);
  
  const contentRef = useRef<HTMLDivElement>(null);
  const upsellTopRef = useRef<HTMLDivElement>(null);
  
  const getUpsellFlow = (initialProductId: string) => {
    const upsellFlows: {[key: string]: string[]} = {
      'launch-guide': ['fast-track', 'accelerator', 'business-mastery'],
      'fast-track': ['accelerator', 'business-mastery'],
      'accelerator': ['fast-track', 'business-mastery'],
      'business-mastery': ['fast-track', 'accelerator'],
      'elite-vip': []
    };
    
    const initialFlow = upsellFlows[initialProductId] || [];
    
    if (user && user.memberships && user.memberships.length > 0) {
      return initialFlow.filter(productId => !user.memberships.includes(productId));
    }
    
    return initialFlow;
  };
  
  const [upsellFlow, setUpsellFlow] = useState<string[]>([]);
  const [cartItems, setCartItems] = useState<{id: string, name: string, price: number}[]>([]);

  const shouldShowOrderBump = (productId?: string) => {
    if (user && user.memberships && user.memberships.includes('ai-prompts')) {
      return false;
    }
    return productId === 'launch-guide';
  };

  useEffect(() => {
    if (id) {
      setSelectedProducts([id]);
      
      if (id) {
        setUpsellFlow(getUpsellFlow(id));
      }
      
      if (membership) {
        setCartItems([{
          id: membership.id,
          name: membership.name,
          price: membership.price
        }]);
      }
      
      setIncludeBonus(false);
      setCurrentUpsell(null);
      setShowUpsell(false);
    }
  }, [id, membership, user]);

  useEffect(() => {
    if (showUpsell && upsellTopRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        if (upsellTopRef.current) {
          upsellTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [showUpsell, currentUpsell]);

  if (!membership) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
        <h1 className="mt-4 text-2xl font-bold">Membership Not Found</h1>
        <p className="mt-2 text-gray-600">The membership you're looking for doesn't exist.</p>
        <Link to="/pricing">
          <Button className="mt-6">View Available Memberships</Button>
        </Link>
      </div>
    );
  }

  const userAlreadyOwns = user && user.memberships && user.memberships.includes(id || '');
  if (userAlreadyOwns) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <Check className="mx-auto h-12 w-12 text-green-500" />
        <h1 className="mt-4 text-2xl font-bold">You Already Own This</h1>
        <p className="mt-2 text-gray-600">You already have access to {membership.name}.</p>
        <Link to="/dashboard">
          <Button className="mt-6">Go to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const calculateTotal = () => {
    const uniqueProductIds = Array.from(new Set(selectedProducts));
    let total = 0;
    
    for (const productId of uniqueProductIds) {
      const product = getMembership(productId);
      if (product) {
        total += product.price;
      }
    }
    
    if (includeBonus && !uniqueProductIds.includes('ai-prompts')) {
      const bonusProduct = getMembership('ai-prompts');
      if (bonusProduct) {
        total += bonusProduct.price;
      }
    }
    
    return total;
  };

  const getPaymentLink = () => {
    const uniqueProductIds = Array.from(new Set(selectedProducts));
    
    if (includeBonus && !uniqueProductIds.includes('ai-prompts')) {
      uniqueProductIds.push('ai-prompts');
    }
    
    const paymentLink = getBundlePaymentLink(uniqueProductIds);
    
    if (paymentLink) {
      return paymentLink;
    }
    
    if (uniqueProductIds.length === 1) {
      const singleProduct = getMembership(uniqueProductIds[0]);
      return singleProduct?.paymentLink || '';
    }
    
    return membership.paymentLink || '';
  };

  const handleContinueToNextUpsell = () => {
    if (!currentUpsell) {
      if (upsellFlow.length > 0) {
        setCurrentUpsell(upsellFlow[0]);
        setShowUpsell(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        finalizeCheckout();
      }
    } else {
      const currentIndex = upsellFlow.indexOf(currentUpsell);
      if (currentIndex >= 0 && currentIndex < upsellFlow.length - 1) {
        setCurrentUpsell(upsellFlow[currentIndex + 1]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        finalizeCheckout();
      }
    }
  };

  const handleAddUpsell = () => {
    if (currentUpsell) {
      const upsellProduct = getMembership(currentUpsell);
      if (!upsellProduct) return;
      
      if (!selectedProducts.includes(currentUpsell)) {
        setSelectedProducts(prev => [...prev, currentUpsell]);
      }
      
      const newCartItems = [...cartItems];
      if (!newCartItems.some(item => item.id === currentUpsell)) {
        newCartItems.push({
          id: upsellProduct.id,
          name: upsellProduct.name,
          price: upsellProduct.price
        });
      }
      setCartItems(newCartItems);
      
      const currentIndex = upsellFlow.indexOf(currentUpsell);
      if (currentIndex >= 0 && currentIndex < upsellFlow.length - 1) {
        setCurrentUpsell(upsellFlow[currentIndex + 1]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        finalizeCheckout();
      }
    }
  };

  const finalizeCheckout = () => {
    setIsLoading(true);
    
    const uniqueProductIds = Array.from(new Set(selectedProducts));
    
    setSelectedProducts(uniqueProductIds);
    
    const uniqueCartItems: {id: string, name: string, price: number}[] = [];
    
    for (const productId of uniqueProductIds) {
      const product = getMembership(productId);
      if (product && !uniqueCartItems.some(item => item.id === productId)) {
        uniqueCartItems.push({
          id: product.id,
          name: product.name,
          price: product.price
        });
      }
    }
    
    if (includeBonus && !uniqueProductIds.includes('ai-prompts')) {
      const bonusProduct = getMembership('ai-prompts');
      if (bonusProduct) {
        uniqueCartItems.push({
          id: bonusProduct.id,
          name: bonusProduct.name,
          price: bonusProduct.price
        });
        uniqueProductIds.push('ai-prompts');
      }
    }
    
    setCartItems(uniqueCartItems);
    
    const paymentLink = getPaymentLink();

    if (paymentLink) {
      window.location.href = paymentLink;
    } else {
      setIsLoading(false);
      toast({
        title: "Payment link not available",
        description: "Please try a different payment method or contact support.",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (includeBonus && !user?.memberships?.includes('ai-prompts')) {
      const bonusProduct = getMembership('ai-prompts');
      if (bonusProduct && !selectedProducts.includes('ai-prompts')) {
        setSelectedProducts(prev => [...prev, 'ai-prompts']);
        setCartItems(prev => [...prev, {
          id: bonusProduct.id,
          name: bonusProduct.name,
          price: bonusProduct.price
        }]);
      }
    }
    
    if (id === 'elite-vip') {
      finalizeCheckout();
      return;
    }
    
    if (upsellFlow.length > 0) {
      setShowUpsell(true);
      setCurrentUpsell(upsellFlow[0]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      finalizeCheckout();
    }
    
    setIsLoading(false);
  };

  if (showUpsell && currentUpsell) {
    const upsellProduct = getMembership(currentUpsell);
    if (!upsellProduct) return null;

    const isProductInCart = selectedProducts.includes(currentUpsell);
    const currentTotal = calculateTotal();
    
    const productTitles: Record<string, {title: string, subtitle: string}> = {
      'fast-track': {
        title: 'AI App Fast-Track', 
        subtitle: 'Build Your First AI App in a Weekend'
      },
      'accelerator': {
        title: 'AI App Accelerator', 
        subtitle: 'Monetize & Scale Your AI App Like a Pro'
      },
      'business-mastery': {
        title: 'AI App Business Mastery', 
        subtitle: 'Master AI App Development & Build a Profitable Business in 90 Days'
      },
      'launch-guide': {
        title: 'The Ultimate AI App Launch Guide',
        subtitle: 'Everything You Need To Launch a Successful App with AI'
      }
    };
    
    const { title, subtitle } = productTitles[currentUpsell] || {
      title: upsellProduct.name,
      subtitle: upsellProduct.description
    };
    
    return (
      <div className="w-full min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4 md:px-8">
        <div ref={upsellTopRef}></div>
        
        <UpsellCard
          productId={currentUpsell}
          price={upsellProduct.price}
          title={title}
          subtitle={subtitle}
          isAdded={isProductInCart}
          onAdd={handleAddUpsell}
          onSkip={handleContinueToNextUpsell}
          currentTotal={currentTotal}
        />
      </div>
    );
  }

  const renderProductDescription = () => {
    const productTitles: Record<string, {title: string, subtitle: string}> = {
      'fast-track': {
        title: 'AI App Fast-Track', 
        subtitle: 'Build Your First AI App in a Weekend'
      },
      'accelerator': {
        title: 'AI App Accelerator', 
        subtitle: 'Monetize & Scale Your AI App Like a Pro'
      },
      'business-mastery': {
        title: 'AI App Business Mastery', 
        subtitle: 'Master AI App Development & Build a Profitable Business in 90 Days'
      },
      'launch-guide': {
        title: 'AI App Launch Guide',
        subtitle: 'Everything You Need To Launch a Successful App with AI'
      }
    };

    const { title = membership.name, subtitle = membership.description } = productTitles[id || ''] || {};

    return (
      <div className="space-y-6 bg-white rounded-lg p-6 shadow-sm mb-6">
        <div className="space-y-3 text-center">
          <h2 className="text-3xl font-bold text-center">{title}</h2>
          <p className="text-lg font-medium text-indigo-600 text-center">{subtitle}</p>
          <p className="text-sm text-gray-700 leading-relaxed text-center">
            Turn Your Idea into an AI-Powered App – Follow a proven roadmap to build, launch, and fund your AI-driven app.
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-6 border">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 text-left">What You'll Learn:</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">AI-Powered Market Research – Validate your idea fast using AI tools to analyze trends and user demand.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">Fully Build & Launch Your AI Mobile App in 30 Days or Less – Step-by-step guidance to develop and deploy your AI-powered app fast.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">Stand Out with AI Innovation – Use AI to outperform competitors and deliver a smarter user experience.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">Monetize with AI Strategies – Unlock high-profit revenue models powered by AI automation and analytics.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">Secure Funding with AI Insights – Leverage AI-driven data to craft a pitch investors can't ignore.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">AI-Driven Growth Hacks – Automate marketing, optimize ads, and acquire users at scale.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">Scale Faster with AI Automation – Cut costs and accelerate growth using AI-powered efficiency.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">Avoid Costly Startup Mistakes – Learn industry secrets to save time, money, and headaches.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-gray-700 text-left">Exclusive AI Tools & Founder Network – Get investor-ready templates, AI resources, and expert connections.</span>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 text-left">
          <h1 className="text-3xl font-bold text-left">{membership.name}</h1>
          <p className="text-gray-600 mt-2 text-left">{membership.productTitle || membership.description}</p>
        </div>
        
        {renderProductDescription()}
        
        <Card>
          <CardHeader>
            <CardTitle className="text-left">Complete Your Purchase</CardTitle>
            <CardDescription className="text-left">
              You're just one step away from unlocking premium content
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-lg border p-4 bg-gray-50">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-left">{membership.name}</h3>
                    <p className="text-gray-600 text-sm mt-1 text-left">{membership.description}</p>
                    <div className="mt-2 text-left">
                      {membership.features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-700 mt-1">
                          <Check className="h-4 w-4 text-green-500" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">${membership.price}</div>
                    <div className="text-sm text-gray-500">One-time payment</div>
                  </div>
                </div>
              </div>
              
              {shouldShowOrderBump(id) && (
                <div className="rounded-lg border-2 border-orange-300 p-4 bg-orange-50">
                  <div className="mb-3 bg-orange-100 -mt-7 -mx-2 px-4 py-2 rounded-full inline-block">
                    <h3 className="font-bold text-red-600 flex items-center gap-2 animate-[blink_1.5s_step-start_infinite]">
                      <Sparkles className="h-4 w-4" />
                      Wait! Take Full Advantage of AI App Development Hacks
                    </h3>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="pt-1">
                      <Checkbox 
                        id="order-bump" 
                        checked={includeBonus} 
                        onCheckedChange={(checked) => setIncludeBonus(checked === true)}
                      />
                    </div>
                    <div>
                      <label 
                        htmlFor="order-bump" 
                        className="font-bold cursor-pointer flex items-center gap-2"
                      >
                        Full-Proof AI App Development Script & 100 AI Prompts
                        <span className="bg-green-100 text-green-800 text-xl font-bold px-2 py-0.5 rounded">
                          $19.00
                        </span>
                      </label>
                      <p className="text-sm text-gray-700 mt-1 text-left">
                        Use this complete AI App Development copy and paste swipe file to tell AI exactly how to design, code, and publish it in the app stores. Plus, 100 full-proof AI prompts to make it completely custom!
                      </p>
                      <p className="text-sm text-gray-700 mt-1 font-medium text-left">
                        This information will save you months of time and tens of thousands of dollars in development fees!
                      </p>
                      <ul className="mt-2 space-y-1">
                        <li className="flex items-center gap-2 text-sm">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>AI Development Copy and Paste Swipe File</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>100 Full-Proof AI Prompts to build the perfect app</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>Case study: How I Built an AI-Powered App in 24 Hours</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full py-6 text-lg flex items-center justify-center gap-2" 
                  disabled={isLoading}
                >
                  <CreditCard className="h-5 w-5" />
                  {isLoading ? 'Processing...' : `Complete Purchase - $${calculateTotal()}`}
                </Button>
              </div>
              
              <div className="text-center text-sm text-gray-500">
                By completing your purchase, you agree to our{' '}
                <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Purchase;
